import type { ChartThemeTokens } from 'design-system';

export type AppearanceMode =
  | 'regular'
  | 'fullscreen-light'
  | 'fullscreen-dark'
  | 'export'
  | 'preview';

export type ChartAppearance = {
  background: string;
  foreground: string;
  mutedForeground: string;
  disabledForeground: string;
  grid: string;
  border: string;
  accent: string;
  accentForeground: string;
  tooltipBackground: string;
  tooltipForeground: string;
  navigatorBackground: string;
  hoverBackground: string;
  palette: readonly string[];
  seriesPalettes: {
    vibrant: readonly string[];
    monoBlue: readonly string[];
    balanced: readonly string[];
  };
  watermark: string;
  fontFamily: string;
};

export const createChartAppearance = (
  tokens: ChartThemeTokens,
  mode: AppearanceMode
): ChartAppearance => {
  const palette = mode === 'fullscreen-light' ? tokens.light : tokens.dark;

  return {
    ...palette,
    seriesPalettes: tokens.seriesPalettes,
    fontFamily: tokens.fontFamily
  };
};

const normalizePaletteIndex = (index: number, length: number): number =>
  ((index % length) + length) % length;

export const getSeriesPaletteColor = (
  appearance: ChartAppearance,
  combinedIndex: number,
  fallbackColor = appearance.foreground
): string => {
  if (appearance.palette.length === 0) return fallbackColor;

  return appearance.palette[normalizePaletteIndex(combinedIndex, appearance.palette.length)];
};

export const createAppearanceOptions = (appearance: ChartAppearance) => ({
  chart: {
    backgroundColor: appearance.background,
    style: { fontFamily: appearance.fontFamily }
  },
  xAxis: {
    labels: { style: { color: appearance.foreground } },
    gridLineColor: appearance.grid,
    lineColor: appearance.grid,
    tickColor: appearance.grid
  },
  yAxis: {
    labels: { style: { color: appearance.foreground } },
    gridLineColor: appearance.grid
  }
});
